import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import { useLogo } from '../../context/LogoContext';
import { ScrollArea } from '../ui/scroll-area';
import { Shield, ChevronRight, Zap } from 'lucide-react';

export default function SidebarContent({
  user: sidebarUser,
  items,
  pathname,
  onNavigate,
}) {
  const { branding } = useBranding();
  const { user: authUser } = useAuth();
  const { logoUrl: globalLogo } = useLogo();

  const appName = branding?.app_name || 'SpillConsult EMS';
  const token = localStorage.getItem('ems_token');
  const isAdmin = authUser?.roles?.includes('super_admin');
  const shouldUsePublic = !isAdmin || !token;
  const brandingLogoUrl = branding?.logo_file_id
    ? shouldUsePublic
      ? `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${branding.logo_file_id}/public?v=${encodeURIComponent(branding?.updated_at || '')}`
      : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${branding.logo_file_id}?token=${encodeURIComponent(token)}&v=${encodeURIComponent(branding?.updated_at || '')}`
    : null;
  // Prefer global hub logo, fallback to branding-specific logo
  const logoUrl = globalLogo || brandingLogoUrl;

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <Link to="/dashboard" className="flex items-center gap-3" onClick={onNavigate} data-testid="sidebar-logo-link">
          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center overflow-hidden" data-testid="sidebar-logo-wrapper">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="w-full h-full object-contain p-1" data-testid="sidebar-logo-image" />
            ) : (
              <Shield className="w-6 h-6 text-primary" />
            )}
          </div>
          <div className="min-w-0">
            <h1 className="font-secondary font-bold text-lg tracking-tight truncate">{appName}</h1>
            <p className="text-xs text-muted-foreground truncate">Exercise Management System</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {/* Hub link */}
          <Link
            to="/hub"
            onClick={onNavigate}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-all mb-2 border border-dashed border-border"
            data-testid="sidebar-hub-link"
          >
            <Zap className="w-4 h-4" />
            <span className="font-medium text-xs">Back to Hub</span>
          </Link>

          {items.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.path || (item.path !== '/dashboard' && pathname.startsWith(item.path));

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>
      </ScrollArea>

      {/* User Info */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            {sidebarUser?.picture ? (
              <img src={sidebarUser.picture} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <span className="font-semibold text-primary">{sidebarUser?.name?.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{sidebarUser?.name}</p>
            <p className="text-xs text-muted-foreground truncate capitalize">{sidebarUser?.roles?.join(', ')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
